import { Button as MuiButton, ButtonProps } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { ArrowForward } from "@mui/icons-material";

export type ButtonTone = "primary" | "secondary" | "ghost" | "accent" | "outline" | "dark";

interface Props extends Omit<ButtonProps, "color"> {
  tone?: ButtonTone;
  withArrow?: boolean;
}

const TONE_SX: Record<ButtonTone, SxProps<Theme>> = {
  primary: {
    bgcolor: "primary.main",
    color: "#fff",
    "&:hover": { bgcolor: "primary.main", filter: "brightness(0.92)" },
    "&.Mui-disabled": { bgcolor: "primary.main", opacity: 0.4, color: "#fff" },
  },
  secondary: {
    bgcolor: "primary.light",
    color: "primary.dark",
    "&:hover": { bgcolor: "primary.base" },
  },
  ghost: {
    bgcolor: "transparent",
    color: "primary.dark",
    "&:hover": { bgcolor: "action.hover" },
  },
  accent: {
    bgcolor: "primary.accent",
    color: "#fff",
    "&:hover": { opacity: 0.9, bgcolor: "primary.accent" },
  },
  // Blanco con borde frío: el "Cancelar" de los modales del diseño, que necesita leerse como
  // botón (a diferencia de `ghost`, que es un texto sin contorno).
  outline: {
    bgcolor: "background.paper",
    color: "primary.dark",
    border: "1px solid #D5E1EC",
    "&:hover": { bgcolor: "#F7FAFD", borderColor: "#D5E1EC" },
    "&.Mui-disabled": { bgcolor: "background.paper", borderColor: "#D5E1EC", opacity: 0.5 },
  },
  // Navy: acción destacada pero neutral, como el "Sí, descartar" del diseño.
  dark: {
    bgcolor: "primary.dark",
    color: "#fff",
    "&:hover": { bgcolor: "primary.dark", filter: "brightness(1.25)" },
    "&.Mui-disabled": { bgcolor: "primary.dark", opacity: 0.4, color: "#fff" },
  },
};

export const Button = ({ tone = "primary", withArrow, children, sx, endIcon, ...rest }: Props) => (
  <MuiButton
    variant={tone === "ghost" ? "text" : "contained"}
    disableElevation
    sx={[
      { borderRadius: "8px", textTransform: "none", fontWeight: 600, px: 3 },
      TONE_SX[tone],
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    endIcon={withArrow ? <ArrowForward fontSize="small" /> : endIcon}
    {...rest}
  >
    {children}
  </MuiButton>
);
